import { z } from 'zod';
import { E164PhoneSchema, TimestampSchema, UuidSchema } from './common';
import { LeadSourceSchema, LeadStatusSchema } from './leads';

export const CampaignAudienceKindSchema = z.enum(['lead-segment', 'csv-upload', 'static-list']);
export type CampaignAudienceKind = z.infer<typeof CampaignAudienceKindSchema>;

export const CampaignDistributionStrategySchema = z.enum([
  'rotating',
  'round-robin',
  'random',
  'warmup-blocks',
]);
export type CampaignDistributionStrategy = z.infer<typeof CampaignDistributionStrategySchema>;

export const CampaignAudienceLeadSegmentFiltersSchema = z.object({
  tags: z.array(z.string().min(1)).optional(),
  status: z.array(LeadStatusSchema).optional(),
  source: z.array(LeadSourceSchema).optional(),
});
export type CampaignAudienceLeadSegmentFilters = z.infer<
  typeof CampaignAudienceLeadSegmentFiltersSchema
>;

export const CampaignAudienceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('lead-segment'),
    filters: CampaignAudienceLeadSegmentFiltersSchema,
  }),
  z.object({
    kind: z.literal('csv-upload'),
    csvJobId: z.string().min(1),
  }),
  z.object({
    kind: z.literal('static-list'),
    // Telefones E.164 colados pelo operador. O backend faz upsert dos leads
    // (onConflictDoNothing) e resolve pra leadIds no momento do disparo —
    // antes a UI fabricava UUIDs falsos que o backend nunca encontrava.
    phones: z.array(E164PhoneSchema).min(1),
  }),
]);
export type CampaignAudience = z.infer<typeof CampaignAudienceSchema>;

export const CampaignStatusSchema = z.enum([
  'draft',
  'scheduled',
  'queued',
  'running',
  'paused',
  'completed',
  'cancelled',
  'failed',
]);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

export const CampaignProgressSchema = z.object({
  totalRecipients: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  currentTps: z.number().nonnegative().optional(),
});
export type CampaignProgress = z.infer<typeof CampaignProgressSchema>;

/** Falhas reais de uma campanha, agrupadas por código de erro da Meta. */
export const CampaignFailureReasonSchema = z.object({
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  count: z.number().int().nonnegative(),
});
export type CampaignFailureReason = z.infer<typeof CampaignFailureReasonSchema>;

export const CampaignSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  name: z.string().min(1).max(100),
  templateId: UuidSchema,
  audience: CampaignAudienceSchema,
  connectionsIds: z.array(UuidSchema).min(1),
  distributionStrategy: CampaignDistributionStrategySchema,
  tps: z.number().int().positive().optional(),
  dailyLimitPerConnection: z.number().int().positive().optional(),
  scheduledAt: TimestampSchema.optional(),
  autoReplyAgentId: UuidSchema.optional(),
  status: CampaignStatusSchema,
  progress: CampaignProgressSchema,
  /** Só populado no detalhe (getCampaign) — agregação real de messages failed. */
  failureReasons: z.array(CampaignFailureReasonSchema).optional(),
  startedAt: TimestampSchema.optional(),
  completedAt: TimestampSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CreateCampaignInputSchema = z.object({
  name: z.string().min(1).max(100),
  templateId: UuidSchema,
  audience: CampaignAudienceSchema,
  connectionsIds: z.array(UuidSchema).min(1),
  distributionStrategy: CampaignDistributionStrategySchema,
  tps: z.number().int().positive().optional(),
  dailyLimitPerConnection: z.number().int().positive().optional(),
  scheduledAt: TimestampSchema.optional(),
  autoReplyAgentId: UuidSchema.optional(),
});
export type CreateCampaignInput = z.infer<typeof CreateCampaignInputSchema>;

export const UpdateCampaignInputSchema = CreateCampaignInputSchema.partial();
export type UpdateCampaignInput = z.infer<typeof UpdateCampaignInputSchema>;

export const CampaignsListQuerySchema = z.object({
  /** Filtra por status — aplicado no SQL para que `total` e a paginação
   * reflitam o recorte (antes o filtro rodava só sobre a página carregada). */
  status: CampaignStatusSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type CampaignsListQuery = z.infer<typeof CampaignsListQuerySchema>;

export const CampaignsListResponseSchema = z.object({
  campaigns: z.array(CampaignSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type CampaignsListResponse = z.infer<typeof CampaignsListResponseSchema>;

export const CsvUploadJobStatusSchema = z.enum(['pending', 'processing', 'completed', 'failed']);
export type CsvUploadJobStatus = z.infer<typeof CsvUploadJobStatusSchema>;

export const CsvUploadJobErrorSchema = z.object({
  row: z.number().int().nonnegative(),
  error: z.string().min(1),
});
export type CsvUploadJobError = z.infer<typeof CsvUploadJobErrorSchema>;

export const CsvUploadJobSchema = z.object({
  id: UuidSchema,
  status: CsvUploadJobStatusSchema,
  leadCount: z.number().int().nonnegative(),
  errors: z.array(CsvUploadJobErrorSchema).optional(),
  createdAt: TimestampSchema,
});
export type CsvUploadJob = z.infer<typeof CsvUploadJobSchema>;

export const CsvUploadResultSchema = z.object({
  job: CsvUploadJobSchema,
  imported: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(z.object({ row: z.number().int().nonnegative(), message: z.string() })),
});
export type CsvUploadResult = z.infer<typeof CsvUploadResultSchema>;
